import React, { useState } from 'react';
import { sendContactMessage } from '../api/contactApi';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const result = await sendContactMessage(formData);

    setIsSubmitting(false);

    if (result.success) {
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
      // Reset status after 3 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 3000);
    } else {
      setSubmitStatus('error');
      // Reset error status after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
  };

  const handleContactClick = (type) => {
    if (type === 'phone') {
      window.location.href = 'tel:+1234567890';
    } else if (type === 'email') {
      window.location.href = 'mailto:info@nesredinfo.com';
    }
  };

  return (
    <section id="contact" className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-20">
          <div>
            <div className="inline-block bg-gray-100 px-6 py-3 rounded-full shadow-sm mb-6">
              <span className="text-green-600 font-semibold">Get In Touch</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-8">
              Let's <span className="text-gradient">Connect</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-12">
              Ready to start your next project? We're here to help. Send us a message and we'll get back to you within 24 hours.
            </p>

            <div className="space-y-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center text-green-600 text-2xl">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Our Office</div>
                  <div className="text-gray-600">123 Tech Street, Innovation City, NY 10001</div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 text-2xl">
                  <i className="fas fa-phone"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Phone</div>
                  <div className="text-gray-600">+1 (555) 123-4567</div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 text-2xl">
                  <i className="fas fa-envelope"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900">Email</div>
                  <div className="text-gray-600">info@nexatech.com</div>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Follow Us</h3>
              <div className="flex gap-4">
                <a href="#" className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-green-500 hover:text-white transition-all">
                  <i className="fab fa-linkedin"></i>
                </a>
                <a href="#" className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-blue-500 hover:text-white transition-all">
                  <i className="fab fa-twitter"></i>
                </a>
                <a href="#" className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-purple-500 hover:text-white transition-all">
                  <i className="fab fa-github"></i>
                </a>
                <a href="#" className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-pink-500 hover:text-white transition-all">
                  <i className="fab fa-dribbble"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-12 rounded-3xl shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Send us a message</h3>
            
            {submitStatus === 'success' && (
              <div className="bg-green-100 border border-green-500 text-green-700 p-6 rounded-2xl mb-8">
                <div className="flex items-center gap-3">
                  <i className="fas fa-check-circle text-xl"></i>
                  <div>
                    <div className="font-bold">Message Sent!</div>
                    <div className="text-sm">Thank you for reaching out. We'll get back to you soon.</div>
                  </div>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-100 border border-red-500 text-red-700 p-6 rounded-2xl mb-8">
                <div className="flex items-center gap-3">
                  <i className="fas fa-exclamation-circle text-xl"></i>
                  <div>
                    <div className="font-bold">Error Sending Message</div>
                    <div className="text-sm">Something went wrong. Please try again later.</div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-6 py-4 border border-gray-300 rounded-xl bg-white input-focus"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-6 py-4 border border-gray-300 rounded-xl bg-white input-focus"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-6 py-4 border border-gray-300 rounded-xl bg-white input-focus"
                  placeholder="Project Inquiry"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-6 py-4 border border-gray-300 rounded-xl bg-white input-focus resize-none"
                  placeholder="Tell us about your project..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full magnetic-btn bg-green-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-green-600 transition-all shadow-lg hover:shadow-green-500/30"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="loader-spinner w-6 h-6 border-2 border-white border-t-transparent"></div>
                    <span>Sending...</span>
                  </div>
                ) : (
                  <span>Send Message</span>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
